import { useCallback, useEffect, useState } from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import axios from "axios";

// A resource is loaded once per URL/account; failures wait for an explicit retry.
export function useAuthenticatedResource(path, select, { enabled = true, retainData = false } = {}) {
    const { instance, accounts } = useMsal();
    const authenticated = useIsAuthenticated();
    const accountId = accounts[0]?.homeAccountId;
    const [revision, setRevision] = useState(0);
    const [state, setState] = useState({ data: null, isLoading: true, isRefreshing: false, error: null, updatedAt: null });
    const reload = useCallback(() => setRevision(value => value + 1), []);

    useEffect(() => {
        if (!authenticated || !enabled) return;
        const controller = new AbortController();
        const belongsToResource = previous => previous.path === path && previous.select === select
            && previous.instance === instance && previous.accountId === accountId;
        const identity = { path, select, instance, accountId };
        setState(previous => {
            const data = retainData && belongsToResource(previous) ? previous.data : null;
            return { ...identity, data, isLoading: data === null, isRefreshing: data !== null,
                error: null, updatedAt: data !== null ? previous.updatedAt : null };
        });

        async function load() {
            try {
                const token = await instance.acquireTokenSilent({
                    scopes: [`api://${process.env.REACT_APP_CLIENT_ID}/user_impersonation`],
                });
                if (controller.signal.aborted) return;
                const response = await axios.get(`${process.env.REACT_APP_ENDPOINT_URL}${path}`, {
                    headers: { Authorization: `Bearer ${token.accessToken}` },
                    signal: controller.signal,
                });
                const data = select ? select(response.data) : response.data;
                if (!controller.signal.aborted) setState({ ...identity, data, isLoading: false, isRefreshing: false, error: null, updatedAt: Date.now() });
            } catch (error) {
                if (controller.signal.aborted) return;
                console.error(`Unable to load ${path}:`, error);
                setState(previous => {
                    const data = retainData && belongsToResource(previous) ? previous.data : null;
                    return { ...identity, data, isLoading: false, isRefreshing: false, error,
                        updatedAt: data !== null ? previous.updatedAt : null };
                });
            }
        }

        load();
        return () => controller.abort();
    }, [path, instance, authenticated, accountId, revision, select, enabled, retainData]);

    const current = authenticated && enabled && state.path === path && state.select === select
        && state.instance === instance && state.accountId === accountId;
    return current ? { ...state, reload } :
        { data: null, isLoading: authenticated && enabled, isRefreshing: false, error: null, updatedAt: null, reload };
}
